// Phase 0094-P1 — Payroll Foundation API client.
import apiClient from '@/shared/api/client'

export interface PayrollHead {
  id: string
  head_code: string
  head_name: string
  head_type: 'EARNING' | 'DEDUCTION' | 'EMPLOYER_CONTRIBUTION' | 'INFORMATION_ONLY'
  calculation_type: string
  fixed_amount: number | null
  percentage_of_head_code: string | null
  display_order: number
  taxable_flag: boolean
  statutory_flag: boolean
  active: boolean
  system_seeded: boolean
  admin_modified_at: string | null
}

export interface SalaryStructureLine {
  id?: string
  head_code: string
  amount?: number | null
  percentage?: number | null
  formula_expression?: string | null
}

export interface SalaryStructure {
  id: string
  employee_id: string
  salary_type: string
  effective_from: string
  effective_to: string | null
  basic_amount: number | null
  monthly_ctc: number | null
  gross_monthly: number | null
  per_day_rate: number | null
  per_hour_rate: number | null
  ot_rate: number | null
  approval_status: string
  active: boolean
  lines?: SalaryStructureLine[]
}

export interface PayrollRun {
  id: string
  period_id: string
  plant_id: string
  department_id: string | null
  run_type: string
  run_status: string
  total_employees: number
  total_earnings: number
  total_deductions: number
  total_net_payable: number
  total_employer_contribution: number
  approved_at: string | null
  locked_at: string | null
  remarks: string | null
}

export interface PayrollRunEmployee {
  id: string
  employee_id: string
  department_id: string | null
  salary_type: string | null
  payable_days: number
  present_days: number
  lwp_days: number
  ot_hours: number
  gross_earnings: number
  total_deductions: number
  net_payable: number
  calculation_status: string
  review_required: boolean
  error_message: string | null
  locked: boolean
}

export interface SalaryRegister {
  run_id: string
  run_status: string
  heads: Array<{ head_code: string; head_name: string; head_type: string }>
  rows: Array<{
    employee_id: string
    employee_code: string | null
    employee_name: string | null
    department: string | null
    salary_type: string | null
    payable_days: number
    present_days: number
    lwp_days: number
    ot_hours: number
    heads: Record<string, number>
    gross: number
    total_deductions: number
    net_payable: number
    bank_account_masked: string | null
    calculation_status: string
  }>
  totals: { gross: number; deductions: number; net: number; employees: number }
}

const P = '/payroll'

// HR-004 follow-up — Shortage Rate Rules (set by HR/Payroll admin,
// read by supervisor as the read-only rate during allocation).
export interface ShortageRateRule {
  id: string
  plant_id: string
  machine_type: string
  side_count: 3 | 4
  extra_total_amount: string  // as string from server, parse to Number for math
  effective_from: string
  effective_to: string | null
  is_active: boolean
  remarks: string | null
}

export interface ShortageRateRuleCreate {
  plant_id: string
  machine_type: string
  side_count: 3 | 4
  extra_total_amount: number | string
  effective_from: string
  effective_to?: string | null
  remarks?: string | null
}

export interface ShortageRateRulePatch {
  extra_total_amount?: number | string
  effective_to?: string | null
  is_active?: boolean
  remarks?: string | null
}

export const shortageRatesApi = {
  list: async (params: {
    plant_id: string; machine_type?: string;
    side_count?: number; is_active?: boolean
  }) =>
    (await apiClient.get<ShortageRateRule[]>(`${P}/shortage-rates`, { params })).data,
  lookup: async (params: {
    plant_id: string; machine_type: string; side_count: number; as_of?: string
  }) =>
    (await apiClient.get<ShortageRateRule | null>(`${P}/shortage-rates/lookup`,
      { params })).data,
  create: async (body: ShortageRateRuleCreate) =>
    (await apiClient.post<ShortageRateRule>(`${P}/shortage-rates`, body)).data,
  patch: async (id: string, body: ShortageRateRulePatch) =>
    (await apiClient.patch<ShortageRateRule>(`${P}/shortage-rates/${id}`, body)).data,
  remove: async (id: string) =>
    (await apiClient.delete(`${P}/shortage-rates/${id}`)).data,
}

export const payrollApi = {
  heads: async (params: { head_type?: string; active_only?: boolean } = {}) =>
    (await apiClient.get<PayrollHead[]>(`${P}/heads`, { params })).data,
  listStructures: async (employee_id?: string) =>
    (await apiClient.get<SalaryStructure[]>(`${P}/salary-structures`,
      { params: employee_id ? { employee_id } : {} })).data,
  createStructure: async (body: Record<string, unknown>) =>
    (await apiClient.post<SalaryStructure>(`${P}/salary-structures`, body)).data,
  approveStructure: async (id: string) =>
    (await apiClient.post<SalaryStructure>(`${P}/salary-structures/${id}/approve`)).data,
  ensurePeriod: async (body: { plant_id: string; year: number; month: number }) =>
    (await apiClient.post(`${P}/periods/ensure`, body)).data,
  createRun: async (body: { period_id: string; plant_id: string; department_id?: string; run_type?: string }) =>
    (await apiClient.post<PayrollRun>(`${P}/runs`, body)).data,
  calculateRun: async (id: string) =>
    (await apiClient.post<{ run_id: string; total_employees: number; total_earnings: number; total_deductions: number; total_net_payable: number }>(
      `${P}/runs/${id}/calculate`)).data,
  getRun: async (id: string) =>
    (await apiClient.get<PayrollRun>(`${P}/runs/${id}`)).data,
  listRunEmployees: async (id: string) =>
    (await apiClient.get<PayrollRunEmployee[]>(`${P}/runs/${id}/employees`)).data,
  approveRun: async (id: string) =>
    (await apiClient.post<PayrollRun>(`${P}/runs/${id}/approve`)).data,
  lockRun: async (id: string, reason?: string) =>
    (await apiClient.post<PayrollRun>(`${P}/runs/${id}/lock`, { reason })).data,
  generatePayslips: async (id: string) =>
    (await apiClient.post<{ payslips_generated: number }>(`${P}/runs/${id}/generate-payslips`)).data,
  salaryRegister: async (id: string) =>
    (await apiClient.get<SalaryRegister>(`${P}/runs/${id}/salary-register`)).data,
  addAdjustment: async (body: { employee_id: string; period_id: string; head_code: string; amount: number; adjustment_type: string; reason: string }) =>
    (await apiClient.post(`${P}/adjustments`, body)).data,
  approveAdjustment: async (id: string) =>
    (await apiClient.post(`${P}/adjustments/${id}/approve`)).data,
  createPaymentBatch: async (run_id: string, batch_type = 'BANK_TRANSFER') =>
    (await apiClient.post<{ id: string; batch_no: string; status: string; total_amount: number; total_employees: number }>(
      `${P}/payment-batches`, { run_id, batch_type })).data,
  markBatchPaid: async (batch_id: string, body: { payment_reference?: string; payment_date?: string }) =>
    (await apiClient.post(`${P}/payment-batches/${batch_id}/mark-paid`, body)).data,
  generateFinancePosting: async (run_id: string) =>
    (await apiClient.post<{ staging_rows_created: number }>(`${P}/runs/${run_id}/finance-posting/generate`)).data,

  // ── Phase 0094-P1 addendum: Costing ──
  regenAllocations: async (run_id: string) =>
    (await apiClient.post<{ run_id: string; allocations_created: number; review_required: number; allocated_total: number }>(
      `${P}/runs/${run_id}/cost-allocations/generate`)).data,
  costingDashboard: async (run_id: string) =>
    (await apiClient.get<CostingDashboard>(`${P}/runs/${run_id}/costing/dashboard`)).data,
  deptReport: async (run_id: string) =>
    (await apiClient.get<DeptCostRow[]>(`${P}/runs/${run_id}/costing/department-wise`)).data,
  machineReport: async (run_id: string) =>
    (await apiClient.get<MachineCostRow[]>(`${P}/runs/${run_id}/costing/machine-wise`)).data,
  labourCostPerKg: async (run_id: string) =>
    (await apiClient.get<KgCostRow[]>(`${P}/runs/${run_id}/costing/labour-cost-per-kg`)).data,
  otReport: async (run_id: string) =>
    (await apiClient.get<OTCostRow[]>(`${P}/runs/${run_id}/costing/ot`)).data,
  maintenanceWoReport: async (run_id: string) =>
    (await apiClient.get<MaintWoRow[]>(`${P}/runs/${run_id}/costing/maintenance-wo`)).data,
  contractReport: async (run_id: string) =>
    (await apiClient.get<ContractRow[]>(`${P}/runs/${run_id}/costing/contract-labour`)).data,
  estimatedReview: async (run_id: string) =>
    (await apiClient.get<EstimatedRow[]>(`${P}/runs/${run_id}/costing/estimated-review`)).data,
  reconcile: async (run_id: string) =>
    (await apiClient.get<{ reconciled: boolean; diffs: Record<string, { line_total: number; alloc_total: number; diff: number }> }>(
      `${P}/runs/${run_id}/costing/reconcile`)).data,
  markAllocReviewed: async (allocation_id: string) =>
    (await apiClient.post<{ id: string; calculation_confidence: string; reviewed_at: string | null }>(
      `${P}/cost-allocations/${allocation_id}/mark-reviewed`)).data,
}

export interface CostingDashboard {
  run_id: string
  run_status: string
  totals: {
    total_labour_cost: number
    review_required: number
    reconciled: boolean
    diffs: Record<string, { line_total: number; alloc_total: number; diff: number }>
  }
  by_cost_type: Array<{ cost_type: string; amount: number; rows: number }>
  by_confidence: Array<{ confidence: string; rows: number }>
}

export interface DeptCostRow {
  department_id: string | null
  department_name: string | null
  total: number
  by_cost_type: Record<string, number>
}

export interface MachineCostRow {
  machine_id: string
  machine_code: string | null
  machine_name: string | null
  labour_cost: number
}

export interface KgCostRow extends MachineCostRow {
  production_kg: number | null
  rs_per_kg: number | null
  calculation_confidence: string
}

export interface OTCostRow {
  department_id: string | null
  department_name: string | null
  machine_id: string | null
  machine_code: string | null
  amount: number
}

export interface MaintWoRow {
  work_order_id: string | null
  amount: number
  rows: number
}

export interface ContractRow {
  department_id: string | null
  department_name: string | null
  amount: number
  rows: number
}

export interface EstimatedRow {
  id: string
  employee_id: string
  employee_code: string | null
  employee_name: string | null
  department_id: string | null
  department_name: string | null
  head_code: string
  cost_type: string
  allocated_amount: number
  allocation_method: string
  remarks: string | null
}
